/**
 * SignIn page — split-panel auth (brand panel + form pane).
 * Steps: email → sent (one combined email: magic link + OTP) → success.
 * The BFF sends a single email via Resend containing both the Firebase
 * magic link (generated server-side) and the 6-digit code — the user picks
 * whichever suits them. Link completion + verify-otp → custom token as before.
 * Prototype source: data-page="signin". British English.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OtpInput } from '../components/OtpInput';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { useSession } from '../context/SessionContext';
import { api, type ApiError } from '../lib/api';
import { signInWithToken } from '../lib/useAuth';
import { auth } from '../lib/firebase';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import './SignIn.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'email' | 'sent' | 'done';

export function SignIn() {
  const navigate = useNavigate();
  const session = useSession();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [magicLinkError, setMagicLinkError] = useState(false);
  // Set when we arrive via a magic link but have no saved email (link opened
  // on a different device/browser) — the user confirms their email to finish.
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  const completeLinkSignIn = (emailAddr: string, href: string) => {
    signInWithEmailLink(auth, emailAddr, href)
      .then((cred) => {
        session.setEmail(cred.user.email ?? emailAddr);
        setStep('done');
        setTimeout(() => navigate('/'), 1200);
      })
      .catch((err) => {
        console.error('magic-link sign-in failed:', err);
        setLoading(false);
        setPendingLink(null);
        setStep('email');
        setMagicLinkError(true);
      });
  };

  // ── Combined sign-in — the BFF emails one message with link + code ──
  const submitEmail = () => {
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError('Enter a valid email address.');
      return;
    }
    // Arrived via a magic link without a saved email — the entered address
    // completes the link sign-in rather than requesting a new email.
    if (pendingLink) {
      setLoading(true);
      completeLinkSignIn(email.trim(), pendingLink);
      return;
    }
    setLoading(true);
    // Stored so the magic-link click on this device can complete sign-in.
    window.localStorage.setItem('itk-magic-link-email', email.trim());
    api
      .post('/api/v1/auth/sign-in', {
        email: email.trim(),
        url: window.location.origin + '/signin',
      })
      .then(() => {
        setLoading(false);
        setStep('sent');
        setOtp('');
        startResendCooldown();
      })
      .catch((err: ApiError) => {
        setLoading(false);
        setEmailError(
          err.status === 429
            ? err.message
            : 'Something went wrong sending the email. Please try again.',
        );
        console.error('sign-in request failed:', err);
      });
  };

  const submitCode = () => {
    if (otp.length !== 6) return;
    setLoading(true);
    // Call the BFF verify-otp endpoint → signInWithCustomToken.
    api
      .post<{ ok: boolean; customToken: string; uid: string }>('/api/v1/auth/verify-otp', {
        email: email.trim(),
        code: otp,
      })
      .then(async (res) => {
        if (!res.customToken) throw new Error('No custom token returned');
        // Sign in to Firebase with the custom token.
        await signInWithToken(res.customToken);
        session.setEmail(email.trim());
        setLoading(false);
        setStep('done');
        setTimeout(() => navigate('/'), 1200);
      })
      .catch((err: ApiError) => {
        setLoading(false);
        // 401 carries the BFF's specific message (wrong/expired/max attempts);
        // 429 carries the rate-limit message.
        setOtpError(
          err.status === 401 || err.status === 429
            ? err.message
            : 'Something went wrong. Please try again.',
        );
        console.error('verify-otp failed:', err);
      });
  };

  const startResendCooldown = () => {
    setResendCooldown(30);
    const interval = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const resend = () => {
    if (resendCooldown > 0) return;
    setOtpError(null);
    setOtp('');
    // Re-send the combined sign-in email via the BFF.
    api
      .post('/api/v1/auth/sign-in', {
        email: email.trim(),
        url: window.location.origin + '/signin',
      })
      .catch(() => {
        // Silently fail — the generic success message covers this.
      });
    startResendCooldown();
  };

  const changeEmail = () => {
    setStep('email');
    setOtp('');
    setOtpError(null);
    setMagicLinkError(false);
  };

  // ── Magic-link redirect handler ──
  // On mount, check if we arrived via a magic-link click (the URL contains
  // the sign-in link). Complete sign-in with the saved email, or — if the link
  // was opened on a different device/browser — ask the user to confirm it.
  useState(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const savedEmail = window.localStorage.getItem('itk-magic-link-email') ?? '';
      if (savedEmail) {
        setStep('done');
        completeLinkSignIn(savedEmail, window.location.href);
      } else {
        setPendingLink(window.location.href);
      }
    }
  });

  return (
    <div className="itk-auth">
      <div className="itk-split">
        {/* ── LEFT / COLOUR PANEL ── */}
        <aside className="itk-panel">
          <div className="itk-panel-top">
            <div className="itk-panel-lockup">
              <div className="itk-lockup-name">Idea Toolkit</div>
              <div className="itk-lockup-by">By Innovate Design</div>
            </div>
            <h1 className="itk-panel-head">Safely explore your idea</h1>
            <ul className="itk-feat">
              <li>
                <span className="itk-feat-mark">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="itk-feat-txt itk-feat-name">Learn how to profit from your idea</span>
              </li>
              <li>
                <span className="itk-feat-mark">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="itk-feat-txt itk-feat-name">Decide whether it&rsquo;s worth taking further</span>
              </li>
              <li>
                <span className="itk-feat-mark">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="itk-feat-txt itk-feat-name">Grants and funding alerts</span>
              </li>
            </ul>
          </div>

          <div className="itk-panel-foot itk-tp">
            {/* TrustBox widget — renders the live Trustpilot score.
                Replace the placeholder data-template-id / data-businessunit-id
                with the real values from the business (see index.html bootstrap). */}
            <div
              className="trustpilot-widget"
              data-locale="en-GB"
              data-template-id="TEMPLATE_ID_PLACEHOLDER"
              data-businessunit-id="BUSINESSUNIT_ID_PLACEHOLDER"
              data-style-height="24px"
              data-style-width="100%"
              data-theme="dark"
            >
              <a
                href="https://uk.trustpilot.com/review/innovate-design.co.uk"
                target="_blank"
                rel="noopener"
              >
                Trustpilot
              </a>
            </div>
          </div>
        </aside>

        {/* ── RIGHT / FORM PANE ── */}
        <div className="itk-card">
          {step === 'email' && (
            <section className="itk-step itk-step--active" aria-labelledby="email-title">
              <h2 className="itk-step-title" id="email-title">
                {pendingLink ? 'Confirm your email address' : 'Sign in to Idea Toolkit'}
              </h2>
              <p className="itk-step-sub">
                {pendingLink
                  ? 'To finish signing in, confirm the email address your sign-in link was sent to.'
                  : 'Enter your email and we\u2019ll send a sign-in link and one-time code. No password to remember.'}
              </p>
              <Input
                label="Email address"
                type="email"
                name="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                error={emailError ?? undefined}
                hint="We'll never sell this or send you spam."
              />
              <Button loading={loading} onClick={submitEmail} style={{ marginTop: 16 }}>
                {pendingLink ? 'Finish signing in' : 'Continue'}
              </Button>
              {magicLinkError && (
                <p className="itk-field-err itk-field-err--show" style={{ marginTop: 8 }}>
                  That sign-in link didn&rsquo;t work — it may have expired. Enter your email to
                  request a fresh one.
                </p>
              )}
              <div className="itk-badges">
                <span className="itk-badge">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  Idea Toolkit is secured and encrypted
                </span>
                <span className="itk-badge">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  Trusted by inventors for 25 years
                </span>
              </div>
              <p className="itk-legal">
                By continuing, you agree to Innovate&rsquo;s <a href="/privacy-policy">Terms</a> and{' '}
                <a href="/privacy-policy">Privacy policy</a>.
              </p>
            </section>
          )}

          {step === 'sent' && (
            <section className="itk-step itk-step--active" aria-labelledby="sent-title">
              <h2 className="itk-step-title" id="sent-title">
                Check your inbox
              </h2>
              <p className="itk-step-sub">
                <span className="itk-sent-dot" />
                We sent an email to <span className="itk-to">{email}</span>
              </p>
              <p className="itk-step-sub" style={{ marginTop: 12 }}>
                Click the sign-in link in the email, or enter the 6-digit code below — whichever
                suits you.
              </p>
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  setOtpError(null);
                }}
                error={!!otpError}
              />
              {otpError && (
                <p className="itk-field-err itk-field-err--show">{otpError}</p>
              )}
              <Button
                loading={loading}
                onClick={submitCode}
                disabled={otp.length !== 6}
                style={{ marginTop: 16 }}
              >
                Verify email address
              </Button>
              <div className="itk-help">
                Not seeing the email in your inbox? Check spam or{' '}
                <button type="button" className="itk-linkbtn" onClick={resend} disabled={resendCooldown > 0}>
                  try sending again
                </button>
                {resendCooldown > 0 && <span className="itk-resend-timer"> ({resendCooldown}s)</span>}.
                <div className="itk-help-row">
                  <button type="button" className="itk-linkbtn" onClick={changeEmail}>
                    Sign in with a different email address
                  </button>
                </div>
              </div>
            </section>
          )}

          {step === 'done' && (
            <div className="itk-done itk-done--show">
              <div className="itk-done-tick">
                <svg viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2>You&rsquo;re signed in</h2>
              <p>Taking you to Idea Toolkit&hellip;</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}